import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Hash, Search, Tag as TagIcon, X } from "lucide-react";
import type { NamedItem } from "@/components/excerpts/ExcerptsBulkBar";
import type { LibrarySourceType } from "@/lib/library";
import { SourceTypeFilterPopover } from "./filterBar/SourceTypeFilterPopover";
import { FacetFilterPopover } from "./filterBar/FacetFilterPopover";
import { ActiveFilterChips } from "./filterBar/ActiveFilterChips";

interface ExcerptsFilterBarProps {
  tags: NamedItem[];
  topics: NamedItem[];
  tagFilters: string[];
  topicFilters: string[];
  sourceTypeFilters?: LibrarySourceType[];
  searchQuery: string;
  searchParams: URLSearchParams;
  setSearchParams: (params: URLSearchParams | Record<string, string>) => void;
}

export const ExcerptsFilterBar = ({
  tags,
  topics,
  tagFilters,
  topicFilters,
  sourceTypeFilters = [],
  searchQuery,
  searchParams,
  setSearchParams,
}: ExcerptsFilterBarProps) => {
  const [localQuery, setLocalQuery] = useState(searchQuery);

  useEffect(() => {
    setLocalQuery(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    const handle = setTimeout(() => {
      if (localQuery === searchQuery) return;
      const next = new URLSearchParams(searchParams);
      const trimmed = localQuery.trim();
      if (trimmed) next.set("q", trimmed);
      else next.delete("q");
      setSearchParams(next);
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localQuery]);

  const setFacet = (kind: "tag" | "topic", values: string[]) => {
    const next = new URLSearchParams(searchParams);
    if (values.length === 0) next.delete(kind);
    else next.set(kind, values.join(","));
    setSearchParams(next);
  };

  const toggleFacetValue = (kind: "tag" | "topic", name: string) => {
    const current = kind === "tag" ? tagFilters : topicFilters;
    const lowered = current.map((c) => c.toLowerCase());
    const trimmed = name.trim();
    if (!trimmed) return;
    if (lowered.includes(trimmed.toLowerCase())) {
      setFacet(kind, current.filter((c) => c.toLowerCase() !== trimmed.toLowerCase()));
    } else {
      setFacet(kind, [...current, trimmed]);
    }
  };

  const removeFacetValue = (kind: "tag" | "topic", name: string) => {
    const current = kind === "tag" ? tagFilters : topicFilters;
    setFacet(kind, current.filter((c) => c.toLowerCase() !== name.toLowerCase()));
  };

  const clearFacet = (kind: "tag" | "topic") => setFacet(kind, []);

  const removeSearch = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("q");
    setSearchParams(next);
    setLocalQuery("");
  };

  const toggleSourceType = (value: NonNullable<LibrarySourceType>) => {
    const next = new URLSearchParams(searchParams);
    const currentSet = new Set(sourceTypeFilters.filter(Boolean) as string[]);
    if (currentSet.has(value)) currentSet.delete(value);
    else currentSet.add(value);
    if (currentSet.size === 0) next.delete("type");
    else next.set("type", Array.from(currentSet).join(","));
    setSearchParams(next);
  };

  const clearSourceTypes = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("type");
    setSearchParams(next);
  };

  const clearFilter = () => {
    setSearchParams({});
    setLocalQuery("");
  };

  const activeFilterCount =
    tagFilters.length +
    topicFilters.length +
    (searchQuery ? 1 : 0) +
    sourceTypeFilters.length;

  if (
    topics.length === 0 &&
    tags.length === 0 &&
    tagFilters.length === 0 &&
    topicFilters.length === 0 &&
    !searchQuery &&
    sourceTypeFilters.length === 0
  ) {
    return null;
  }

  const tagFiltersLower = tagFilters.map((t) => t.toLowerCase());
  const topicFiltersLower = topicFilters.map((t) => t.toLowerCase());

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative w-full sm:flex-1 sm:min-w-[200px] sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            type="search"
            placeholder="Search excerpts…"
            value={localQuery}
            onChange={(e) => setLocalQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                setLocalQuery("");
                const next = new URLSearchParams(searchParams);
                next.delete("q");
                setSearchParams(next);
                e.currentTarget.blur();
              }
            }}
            className="h-9 pl-9 pr-8"
            aria-label="Search excerpts"
            data-search-input
          />
          {localQuery && (
            <button
              onClick={() => {
                setLocalQuery("");
                const next = new URLSearchParams(searchParams);
                next.delete("q");
                setSearchParams(next);
              }}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-muted"
            >
              <X className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          )}
        </div>

        <SourceTypeFilterPopover
          selected={sourceTypeFilters}
          onToggle={toggleSourceType}
          onClear={clearSourceTypes}
        />

        {topics.length > 0 && (
          <FacetFilterPopover
            label="Topics"
            Icon={Hash}
            items={topics}
            selectedLower={topicFiltersLower}
            selectedCount={topicFilters.length}
            onToggle={(name) => toggleFacetValue("topic", name)}
            onClear={() => clearFacet("topic")}
            ariaLabel="Filter by topic"
          />
        )}

        {tags.length > 0 && (
          <FacetFilterPopover
            label="Tags"
            Icon={TagIcon}
            items={tags}
            selectedLower={tagFiltersLower}
            selectedCount={tagFilters.length}
            onToggle={(name) => toggleFacetValue("tag", name)}
            onClear={() => clearFacet("tag")}
            ariaLabel="Filter by tag"
          />
        )}
      </div>

      <ActiveFilterChips
        searchQuery={searchQuery}
        sourceTypeFilters={sourceTypeFilters}
        topicFilters={topicFilters}
        tagFilters={tagFilters}
        activeFilterCount={activeFilterCount}
        onRemoveSearch={removeSearch}
        onToggleSourceType={toggleSourceType}
        onRemoveTopic={(name) => removeFacetValue("topic", name)}
        onRemoveTag={(name) => removeFacetValue("tag", name)}
        onClearAll={clearFilter}
      />
    </>
  );
};
